/**
 * Where every map for this firm opens, as a map the administrator pans until it is right.
 *
 * A setting shown as the thing it sets: the view on the small map is the draft, moving it
 * is editing, and saving stores what is on screen. Discard puts the stored view back. Until
 * the firm chooses, the page shows where the maps open anyway — the widest zone, the
 * deployment's centre, or Europe — and says in one sentence which, so nobody wonders why
 * the map is where it is.
 *
 * The search box is the concession to a firm three countries away from wherever the map
 * opened: a name, Enter, and the map is there. Only on Enter, because every request there
 * is a line on the geocoder's invoice.
 */

import { lazy, Suspense, useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { MapHome } from '@taxi/api-client';

import { mapApi, type MapConfiguration } from '../../map/api';
import { geoApi } from '../../orders/api';
import {
  Banner,
  Button,
  EmptyState,
  FieldErrors,
  Flagged,
  Icon,
  Input,
  PageHeader,
  SaveBar,
  Section,
  type FieldProblem,
} from '../../ui';
import { mapHomeApi } from '../api';
import { refusal } from '../errors';
import { notifySetupChanged } from '../setup/events';
import type { HomeTarget, HomeView } from './HomeMap';
import { openingView, sameView } from './homeView';

const HomeMap = lazy(async () => ({ default: (await import('./HomeMap')).HomeMap }));

const CONFIRMATION_MS = 4000;

export function MapPage() {
  const { t, i18n } = useTranslation();

  const [home, setHome] = useState<MapHome | null>(null);
  const [configuration, setConfiguration] = useState<MapConfiguration | null>(null);

  /** What the map is showing now, which is the draft. */
  const [view, setView] = useState<HomeView | null>(null);
  const [target, setTarget] = useState<HomeTarget | null>(null);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<FieldProblem | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [stored, style] = await Promise.all([mapHomeApi.get(), mapApi.configuration()]);

        if (cancelled) return;

        setHome(stored);
        setConfiguration(style);
        setView(openingView(stored, style));
      } catch (failure) {
        if (!cancelled) setError(refusal(failure, t, 'settings.couldNotLoad'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!saved) return;

    const timer = globalThis.setTimeout(() => setSaved(false), CONFIRMATION_MS);

    return () => globalThis.clearTimeout(timer);
  }, [saved]);

  const stored = home === null ? null : openingView(home, configuration);
  const changes = stored !== null && view !== null && !sameView(stored, view) ? 1 : 0;

  function discard() {
    if (stored === null) return;

    setTarget({ ...stored, key: Date.now() });
    setView(stored);
  }

  async function save(choice: HomeView | null) {
    setSaving(true);
    setError(null);

    try {
      const result = await mapHomeApi.save(
        choice === null
          ? { longitude: null, latitude: null, zoom: null }
          : { longitude: choice.longitude, latitude: choice.latitude, zoom: choice.zoom },
      );

      setHome(result);

      // The map follows what was stored — which, after clearing, is wherever the
      // derivation now says — rather than staying where it was left.
      const next = openingView(result, configuration);
      setView(next);
      if (choice === null) setTarget({ ...next, key: Date.now() });

      setSaved(true);
      notifySetupChanged();
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotSave'));
    } finally {
      setSaving(false);
    }
  }

  async function search(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;

    event.preventDefault();

    const text = query.trim();

    if (text.length === 0 || searching) return;

    setSearching(true);
    setNotFound(false);

    try {
      const found = (await geoApi.search(text)).results[0];

      if (found === undefined) {
        setNotFound(true);
        return;
      }

      // A named place is a city or a street; either wants a closer look than a country.
      setTarget({
        longitude: found.location.longitude,
        latitude: found.location.latitude,
        zoom: Math.max(view?.zoom ?? 0, 12),
        key: Date.now(),
      });
    } catch (failure) {
      setError(refusal(failure, t, 'map.home.couldNotSearch'));
    } finally {
      setSearching(false);
    }
  }

  const figures = new Intl.NumberFormat(i18n.resolvedLanguage, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

  return (
    <FieldErrors problem={error} onEdit={() => setError(null)}>
      <>
        <PageHeader title={t('nav.settings.map')} description={t('nav.settingsAbout.map')} />

        {error !== null && <Banner tone="danger">{error.message}</Banner>}
        {saved && <Banner tone="ok">{t('map.home.saved')}</Banner>}

        {home === null || configuration === null || view === null || stored === null ? (
          error === null && <EmptyState>{t('app.loading')}</EmptyState>
        ) : (
          <>
            <Section
              title={t('map.home.sectionTitle')}
              description={t('map.home.sectionHelp')}
              actions={
                home.source === 'set' && (
                  <Button size="sm" variant="quiet" disabled={saving} onClick={() => void save(null)}>
                    {t('map.home.clearChoice')}
                  </Button>
                )
              }
            >
              <div className="flex flex-col gap-3 p-5">
                <div className="relative">
                  <Icon
                    name="search"
                    small
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
                  />
                  <Input
                    aria-label={t('map.home.search')}
                    placeholder={t('map.home.search')}
                    className="pl-8"
                    value={query}
                    disabled={searching}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setNotFound(false);
                    }}
                    onKeyDown={(event) => void search(event)}
                  />
                </div>

                {notFound && <Banner tone="warn">{t('map.home.notFound', { query: query.trim() })}</Banner>}

                {/* The map is the control: a refused view is marked on it, whichever of the
                    three names the server used. */}
                <Flagged id="mapHome" also={['latitude', 'longitude']}>
                <Suspense
                  fallback={
                    <div className="h-90 rounded-card border border-rule bg-surface-2 flex items-center justify-center">
                      <EmptyState>{t('app.loading')}</EmptyState>
                    </div>
                  }
                >
                  <HomeMap
                    configuration={configuration}
                    initial={view}
                    target={target}
                    onMoved={setView}
                    className="h-90 rounded-card border border-rule overflow-hidden"
                  />
                </Suspense>
                </Flagged>

                {/* The figures under the map are read-only on purpose: the map is the
                    control, and two controls for one view would disagree on every drag. */}
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-secondary text-ink-3 max-w-[60ch]">
                    {t(`map.home.source.${changes === 0 ? stored.source : 'moved'}`)}
                  </span>
                  <span className="text-secondary text-ink-2 tabular-nums whitespace-nowrap">
                    {t('map.home.figures', {
                      latitude: figures.format(view.latitude),
                      longitude: figures.format(view.longitude),
                      zoom: Math.round(view.zoom),
                    })}
                  </span>
                </div>
              </div>
            </Section>

            <SaveBar
              changes={changes}
              problem={error?.message}
              busy={saving}
              onDiscard={discard}
              onSave={() => void save(view)}
              discardLabel={t('settings.discard')}
              saveLabel={t('map.home.useThisView')}
            >
              {t('map.home.unsaved')}
            </SaveBar>
          </>
        )}
      </>
    </FieldErrors>
  );
}
